import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import addressImg from "../../../assets/Images/home/address.png";
import contactImg from "../../../assets/Images/home/Contact.png";
import emailImg from "../../../assets/Images/home/Email.png";
import contactttBgImg from "../../../assets/Images/home/contactttt-bg.png";

// ── EmailJS credentials ───────────────────────────────────────────────
const SERVICE_ID  = "service_gnmq7iu";
const TEMPLATE_ID = "template_lu4mjl9";
const PUBLIC_KEY  = "adDZaNUl_gM8HMMV0";
// ──────────────────────────────────────────────────────────────────────

const ADDRESS_ICON = addressImg;
const CONTACT_ICON = contactImg;
const EMAIL_ICON = emailImg;

const BRAND = "#2d3b2f";

const baseInput =
  "w-full bg-white border px-4 py-3 rounded-md text-sm outline-none transition disabled:opacity-60";
const inputCls = (hasError) =>
  `${baseInput} ${
    hasError
      ? "border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-400"
      : "border-gray-300 focus:border-[#2d3b2f] focus:ring-1 focus:ring-[#2d3b2f]"
  }`;

const INITIAL = { name: "", email: "", phone: "", subject: "", message: "" };

const IconImg = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="w-8 h-8 mt-1 flex-shrink-0 object-contain"
    loading="lazy"
  />
);

const InuquiryBanner = () => {
  const formRef = useRef(null);
  const successTimerRef = useRef(null);
  const [form, setForm] = useState(INITIAL);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | sending | success | error

  // Initialise EmailJS once on mount
  useEffect(() => {
    emailjs.init({ publicKey: PUBLIC_KEY });
  }, []);

  // Auto-clear success banner after 6s
  useEffect(() => {
    if (status.state === "success") {
      successTimerRef.current = setTimeout(
        () => setStatus({ state: "idle", msg: "" }),
        6000
      );
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [status.state]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((errs) => {
        const next = { ...errs };
        delete next[name];
        return next;
      });
    }
    if (status.state === "error") setStatus({ state: "idle", msg: "" });
  };

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Please enter your name.";
    else if (form.name.trim().length < 2)
      errors.name = "Name must be at least 2 characters.";

    if (!form.email.trim()) errors.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errors.email = "Please enter a valid email address.";

    if (!form.phone.trim()) errors.phone = "Please enter your phone number.";
    else if (!/^[+\d][\d\s()-]{6,}$/.test(form.phone.trim()))
      errors.phone = "Please enter a valid phone number.";

    if (!form.subject.trim()) errors.subject = "Please enter a subject.";

    if (!form.message.trim()) errors.message = "Please enter a message.";
    else if (form.message.trim().length < 10)
      errors.message = "Message must be at least 10 characters.";

    return errors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStatus({
        state: "error",
        msg: "Please fix the highlighted fields and try again.",
      });
      return;
    }

    setFieldErrors({});
    setStatus({ state: "sending", msg: "" });

    const templateParams = {
      to_email: "marketing2@nutralike.com,marketing2@nutralike.com,info@nutralike.com",
      name: form.name.trim(),
      email: form.email.trim(),
      number: form.phone.trim(),
      phone: form.phone.trim(),
      subject: form.subject.trim(),
      message: form.message.trim(),
      time: new Date().toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    };

    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY,
      });
      setStatus({
        state: "success",
        msg: "Thank you! Your inquiry has been sent successfully. Our team will get back to you shortly.",
      });
      setForm(INITIAL);
    } catch (err) {
      const apiMsg =
        typeof err?.text === "string"
          ? err.text
          : typeof err?.message === "string"
          ? err.message
          : "";
      setStatus({
        state: "error",
        msg:
          apiMsg ||
          "Sorry, we couldn't send your message right now. Please try again in a moment or email us directly at info@nutralike.com.",
      });
    }
  };

  const isSending = status.state === "sending";

  return (
    <div>
      <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <div>
          <img
            src={contactttBgImg}
            alt="Career"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 flex flex-col justify-end">
          <h1 className="text-white text-2xl md:text-4xl font-semibold">Inquire Now</h1>
          <p className="text-gray-200 text-sm md:text-base mt-2 mb-6 max-w-xl leading-relaxed">
            We're here to help! Send us your inquiry and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      <div className="bg-white py-16 md:px-4 px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Left Info */}
            <div className="space-y-8">
              <div className="flex gap-4 items-start">
                <IconImg src={ADDRESS_ICON} alt="Office Address" />
                <div>
                  <h3 className="font-semibold text-[#2d3b2f] tracking-wide">OFFICE ADDRESS</h3>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                    410-11-12, Malbar Prime, Near Bhakti Circle, Nikol-382350,
                    Ahmedabad, Gujarat, India.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <IconImg src={ADDRESS_ICON} alt="Factory Address" />
                <div>
                  <h3 className="font-semibold text-[#2d3b2f] tracking-wide">FACTORY ADDRESS</h3>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                    Survey no. 1582, Swagat-3 Industrial Park, Ahmedabad-Indore
                    Highway, Kuha, Ahmedabad, Gujarat, INDIA
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <IconImg src={CONTACT_ICON} alt="Contact" />
                <div>
                  <h3 className="font-semibold text-[#2d3b2f] tracking-wide">CONTACT</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    +91 7862891281 | +91 9016412992
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <IconImg src={EMAIL_ICON} alt="Email" />
                <div>
                  <h3 className="font-semibold text-[#2d3b2f] tracking-wide">EMAIL</h3>
                  <p className="text-sm text-gray-500 mt-1">
                      bd@nutralike.com | marketing2@nutralike.com
                  </p>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <form ref={formRef} onSubmit={onSubmit} className="space-y-5" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    disabled={isSending}
                    placeholder="Name"
                    aria-invalid={!!fieldErrors.name}
                    className={inputCls(!!fieldErrors.name)}
                  />
                  {fieldErrors.name && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    disabled={isSending}
                    placeholder="Email"
                    aria-invalid={!!fieldErrors.email}
                    className={inputCls(!!fieldErrors.email)}
                  />
                  {fieldErrors.email && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
                  )}
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    disabled={isSending}
                    placeholder="Phone"
                    aria-invalid={!!fieldErrors.phone}
                    className={inputCls(!!fieldErrors.phone)}
                  />
                  {fieldErrors.phone && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.phone}</p>
                  )}
                </div>

                <div>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    disabled={isSending}
                    placeholder="Subject"
                    aria-invalid={!!fieldErrors.subject}
                    className={inputCls(!!fieldErrors.subject)}
                  />
                  {fieldErrors.subject && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.subject}</p>
                  )}
                </div>
              </div>

              <div>
                <textarea
                  rows="5"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  disabled={isSending}
                  placeholder="Message"
                  aria-invalid={!!fieldErrors.message}
                  className={inputCls(!!fieldErrors.message)}
                />
                {fieldErrors.message && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSending}
                className="inline-flex items-center gap-2 border border-[#2d3b2f] text-[#2d3b2f] px-8 py-2.5 rounded-md hover:bg-[#2d3b2f] hover:text-white transition duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSending && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSending ? "SENDING..." : "SEND"}
              </button>

              {status.state === "success" && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-md"
                >
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}

              {status.state === "error" && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default InuquiryBanner;
